
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import axios from 'axios';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  const navigationItems = [
    { label: 'Home', path: '/' },
    {
      label: 'About Us',
      path: '/about',
      dropdown: [
        { label: 'Our History', path: '/about/history' },
        { label: 'Vision & Mission', path: '/about/vision-mission' },
        { label: 'Diocesan Anthem', path: '/about/anthem' },
      ]
    },
    {
      label: 'Leadership',
      path: '/bishop',
      dropdown: [
        { label: 'Our Bishop', path: '/bishop' },
        { label: 'Episcopal Office', path: '/episcopal-office' },
        { label: 'Past Bishops', path: '/past-bishops' },
      ]
    },
    {
      label: 'Clergy & Parishes',
      path: '/clergy',
      dropdown: [
        { label: 'Our Clergy', path: '/clergy' },
        { label: 'Our Parishes', path: '/parishes' },
        { label: 'Archdeaconries', path: '/archdeaconries' },
        { label: 'Retired Priests', path: '/retired-priests' },
      ]
    },
    {
      label: 'Media',
      path: '/media',
      dropdown: [
        { label: 'Photo Gallery', path: '/gallery' },
        { label: 'Video Gallery', path: '/videos' },
      ]
    },
    { label: 'Events', path: '/events' },
    { label: 'News', path: '/news' },
    { label: 'Contact', path: '/contact' },
  ];

  const isActive = (path: string) => {
    return location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
  };

  const [siteIcon, setSiteIcon] = useState<string | null>(null);

  useEffect(() => {
    const fetchSiteIcon = async () => {
      try {
        const response = await axios.get('https://api.ibadansouthanglicandiocese.org.ng/api/v1/site_icon');
        setSiteIcon(response.data); // assuming it's just a URL string
      } catch (error) {
        console.error('Failed to fetch site icon', error);
      }
    };

    fetchSiteIcon();
  }, []);

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
              {siteIcon ? (
                <img src={siteIcon} alt="Site Icon" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-bold text-lg bg-purple-700 w-full h-full flex items-center justify-center">✝</span>
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold text-gray-900">Diocese of Ibadan South</span>
              <span className="text-xs text-purple-600">Anglican Communion</span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <div key={item.label} className="relative group">
                {item.dropdown ? (
                  <div>
                    <button
                      className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive(item.path)
                        ? 'text-purple-600 bg-purple-50'
                        : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                        }`}
                      onMouseEnter={() => setOpenDropdown(item.label)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      {item.label}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                    {openDropdown === item.label && (
                      <div
                        className="absolute left-0 mt-0 pt-3 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50"
                        onMouseEnter={() => setOpenDropdown(item.label)}
                        onMouseLeave={() => setOpenDropdown(null)}
                      >
                        <div className="py-1">
                          {item.dropdown.map((subItem) => (
                            <Link
                              key={subItem.label}
                              to={subItem.path}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600"
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive(item.path)
                      ? 'text-purple-600 bg-purple-50'
                      : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                      }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-purple-600 hover:bg-purple-50"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigationItems.map((item) => (
              <div key={item.label}>
                {item.dropdown ? (
                  <div>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50"
                    >
                      {item.label}
                      <ChevronDown className={`h-4 w-4 transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === item.label && (
                      <div className="pl-4 space-y-1">
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.label}
                            to={subItem.path}
                            className="block px-3 py-2 rounded-md text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                            onClick={() => setIsOpen(false)}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`block px-3 py-2 rounded-md text-base font-medium ${isActive(item.path)
                      ? 'text-purple-600 bg-purple-50'
                      : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                      }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
